// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { TradeDetails } from '@shared/data-access/interfaces/redux/trades';
import type { InternalProposalOperation } from '@shared/data-access/interfaces/api/brokerage/proposals-types';
import { TARGET_PORTFOLIO_CASH_TICKER } from '@shared/data-access/transformers/proposals/map-target-portfolio-request';
import { NUVARA_INVERSIS_INSTITUTION_ID } from '@shared/data-access/utils/nuvara-inversis-institution';

const AMOUNT_DECIMALS = 2;
const QUANTITY_DECIMALS = 4;

const roundAmount = (value: number): number =>
  Math.round(value * 10 ** AMOUNT_DECIMALS) / 10 ** AMOUNT_DECIMALS;

const roundQuantity = (value: number): number =>
  Math.round(value * 10 ** QUANTITY_DECIMALS) / 10 ** QUANTITY_DECIMALS;

/**
 * Converts a TradeDetails to an InternalProposalOperation for BUY operation.
 *
 * For BUY operations:
 *
 * - From_iban: selected origin IBAN (no `cash_account` fallback)
 * - From_ticker/from_isin: {@link TARGET_PORTFOLIO_CASH_TICKER}
 * - To_iban: selected destination IBAN, else {@link NUVARA_INVERSIS_INSTITUTION_ID}
 * - To_ticker/to_isin: ticker/isin of the asset
 * - Account_id: optional for BUY operations
 * - Institution_id: optional for BUY operations
 */
const buildBuyOperation = (trade: TradeDetails): InternalProposalOperation => {
  const amount = trade.local_currency_values?.expected_volume ?? 0;
  const quantity = trade.contracts_number;
  const price = trade.local_currency_values?.price ?? 0;

  return {
    operation_type: 'buy',
    input_type: trade.inputType,
    from_iban: trade.from_iban ?? '',
    from_ticker: TARGET_PORTFOLIO_CASH_TICKER,
    from_isin: TARGET_PORTFOLIO_CASH_TICKER,
    to_iban: trade.to_iban ?? NUVARA_INVERSIS_INSTITUTION_ID,
    to_ticker: trade.ticker,
    to_isin: trade.isin ?? null,
    quantity: roundQuantity(quantity),
    price,
    amount: roundAmount(amount),
    institution_id: trade.institution_id ?? '',
  };
};

/**
 * Converts a TradeDetails to an InternalProposalOperation for SELL operation.
 *
 * For SELL operations:
 *
 * - From_iban: selected origin IBAN, else account_id
 * - From_ticker/from_isin: ticker/isin of the asset
 * - To_iban: selected destination IBAN (no `cash_account` fallback)
 * - To_ticker/to_isin: {@link TARGET_PORTFOLIO_CASH_TICKER}
 */
const buildSellOperation = (trade: TradeDetails): InternalProposalOperation => {
  const amount = trade.local_currency_values?.expected_volume ?? 0;
  const quantity = trade.contracts_number;

  return {
    operation_type: 'sell',
    input_type: trade.inputType,
    from_iban: trade.from_iban ?? trade.account_id!,
    from_ticker: trade.ticker,
    from_isin: trade.isin ?? null,
    to_iban: trade.to_iban ?? '',
    to_ticker: TARGET_PORTFOLIO_CASH_TICKER,
    to_isin: TARGET_PORTFOLIO_CASH_TICKER,
    quantity: roundQuantity(quantity),
    price: trade.local_currency_values?.price ?? 0,
    amount: roundAmount(amount),
    institution_id: trade.institution_id!,
  };
};

/**
 * Converts a TradeDetails to an InternalProposalOperation for TRANSFER operation.
 *
 * For TRANSFER operations:
 *
 * - From_iban: account_id of the source
 * - From_ticker/from_isin: ticker/isin of the asset
 * - To_iban: {@link NUVARA_INVERSIS_INSTITUTION_ID}
 * - To_ticker/to_isin: ticker/isin of the target asset (same as source for same asset
 *   transfer)
 */
const buildTransferOperation = (trade: TradeDetails): InternalProposalOperation => {
  const amount = trade.local_currency_values?.expected_volume ?? 0;
  const quantity = trade.contracts_number;

  return {
    operation_type: 'transfer',
    input_type: trade.inputType,
    from_iban: trade.from_iban ?? trade.account_id!,
    from_ticker: trade.ticker,
    from_isin: trade.isin ?? null,
    to_iban: trade.to_iban ?? NUVARA_INVERSIS_INSTITUTION_ID,
    to_ticker: trade.transfer_target!,
    to_isin: trade.transfer_target!,
    quantity: roundQuantity(quantity),
    price: trade.local_currency_values?.price ?? 0,
    amount: roundAmount(amount),
    institution_id: trade.institution_id!,
  };
};

/**
 * Converts TradeDetails to InternalProposalOperation based on the operation type.
 *
 * @param trade - The trade details to convert (account_id and institution_id are
 *   required for SELL and TRANSFER operations, optional for BUY operations)
 * @returns An InternalProposalOperation with the correct structure
 * @throws Error if operation type is unsupported
 */
export const buildProposalOperation = (
  trade: TradeDetails,
): InternalProposalOperation => {
  switch (trade.operation) {
    case 'BUY': {
      return buildBuyOperation(trade);
    }
    case 'SELL':
      return buildSellOperation(trade);
    case 'TRANSFER': {
      return buildTransferOperation(trade);
    }
    default:
      throw new Error(`Unsupported operation type: ${trade.operation}`);
  }
};

/**
 * Converts an array of TradeDetails to an array of InternalProposalOperation.
 *
 * @param trades - Array of trade details to convert
 * @returns Array of InternalProposalOperation
 */
export const buildProposalOperations = (
  trades: TradeDetails[],
): InternalProposalOperation[] => {
  return trades
    .filter(trade => {
      // For BUY operations, don't filter - allow all
      if (trade.operation === 'BUY') {
        return true;
      }
      // For other operations, require an origin account (from_iban, else
      // account_id) and institution_id
      return !!(trade.from_iban ?? trade.account_id) && !!trade.institution_id;
    })
    .map(trade => buildProposalOperation(trade));
};
